import React from 'react';
import { TomeButton } from '../../primitives/tome';
import type { NearbyQuestGiver } from '../../../systems/npc/npcQuestOffers';

/** While the character's quest log has not arrived: say so, rather than showing the blank page. */
export const JournalLoading: React.FC = () => <section className="quest-journal-empty-detail" aria-label="Quest details" aria-busy="true">
  <span className="quest-journal-kicker">Opening the journal</span>
  <h2>Your pages are on their way</h2>
  <p>Your quests appear here as soon as your character finishes loading.</p>
</section>;

/** The journal failed to draw: say so on its own page, keep the rest of the screen, and offer another try. */
export const JournalError: React.FC<{ onRetry: () => void }> = ({ onRetry }) => <div className="quest-journal">
  <section className="quest-journal-empty-detail" role="alert" aria-label="Quest details">
    <span className="quest-journal-kicker">A smudged page</span>
    <h2>This page would not open</h2>
    <p>Something in your journal could not be drawn. Your quests are safe: the server keeps them, and the tracker still shows them.</p>
    <div><TomeButton tone="primary" onClick={onRetry}>Try again</TomeButton></div>
  </section>
</div>;

/** "Elder Wynn has", "Elder Wynn and Maren have", "Elder Wynn, Maren and 2 others have" (featured givers first). */
export function giverSentence(givers: readonly NearbyQuestGiver[]): string {
  const names = [...givers].sort((a, b) => Number(b.featured) - Number(a.featured)).map(g => g.name);
  const shown = names.length > 3 ? [...names.slice(0, 2), `${names.length - 2} others`] : names;
  const joined = shown.length <= 2 ? shown.join(' and ') : `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`;
  return `${joined} ${names.length === 1 ? 'has' : 'have'} work for you here`;
}

/** Under an empty Active chapter: who on this map would offer a quest now (they already wear a '!'). */
export const JournalNearbyGivers: React.FC<{ givers: readonly NearbyQuestGiver[] }> = ({ givers }) => givers.length === 0 ? null
  : <p className="quest-journal-nearby" data-testid="quest-journal-nearby"><strong aria-hidden>!</strong> {giverSentence(givers)}.</p>;

/**
 * The blank page: says what the journal is for and points at safe, known ways to find work. `givers` are
 * the people on this map already wearing a '!', so naming them reveals nothing the world does not show.
 */
export const JournalEmpty: React.FC<{ givers?: readonly NearbyQuestGiver[] }> = ({ givers = [] }) =>
  <section className="quest-journal-empty-detail" aria-label="Quest details">
    <span className="quest-journal-kicker">A blank page</span>
    <h2>Your journey starts with a conversation</h2>
    <ul className="quest-journal-hints">
      {givers.length > 0
        ? <li data-testid="quest-journal-nearby-givers"><strong>!</strong> {giverSentence(givers)}. Look for the <strong>!</strong> above
          them, walk up and press <kbd>E</kbd> to talk.</li>
        : <li><strong>!</strong> above someone means they have work for you. Walk up and press <kbd>E</kbd> to talk.</li>}
      <li>Accepted quests are written here with their progress, who to report to, and what they pay.</li>
      <li>Open the World Atlas with <kbd>M</kbd> to see where the roads go.</li>
    </ul>
  </section>;
